import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Loading } from "../../../../components/common";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  ValidationSchemaConfirm,
  ValidationSchemaEmail,
  ValidationSchemaEnable,
  ValidationSchemaRole,
} from "../../../../conf/users";
import Request from "../../../../core/requests";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "store/user.store";
import UserDetailForm from "forms/users/UserDetailForm.component";
import { openSnackbar } from "store/slices/snackbar";
import SideDialogComponent from "components/dialog/SideDialog.component";
import { Typography } from "@mui/material";
import TabsComponent from "components/tabs/Tabs.component";
import UserRoleForm from "forms/users/UserRoleForm.component";
import UserEnableForm from "forms/users/UserEnableForm.component";
import { refreshToken } from "store/user.store";
import { useTranslation } from "react-i18next";
import _ from 'lodash'
import UserConfirmForm from "forms/users/UserConfirmForm.component";

const UpdateUserContainer = ({
  userUp,
  open,
  handleCloseDialog = () => { },
  setSuccess = () => { },
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    token,
    refresh_token,
    error: errorRefresh,
    loading: loadingRefresh,
    user
  } = useSelector((state) => state.user);
  const [loading, setLoading] = useState(false);
  const [id, setId] = useState(userUp?.userId);
  const [valueTab, setValueTab] = useState(0);
  const [validationSchema, setValidationSchema] = useState(
    ValidationSchemaEmail
  );
  const [isFirstRefresh, setIsFirstRefresh] = useState(true);
  const [dataSubmit, setDataSubmit] = useState(null);

  const options = useMemo(
    () => [
      { label: "Visitor", value: "visitor" },
      { label: "Operator", value: "operator" },
      { label: "Superuser", value: "superuser" },
    ],
    []
  );

  const handleChangeTab = useCallback((event, newValue) => {
    setValueTab(newValue);
  }, []);

  const updateForm = useCallback(() => {
    if (userUp) {
      setId(userUp?.userId);
      switch (valueTab) {
        case 0:
          setValue("email", userUp?.email);
          setValue("firstName", userUp?.firstName);
          setValue("lastName", userUp?.lastName);
          break;
        case 1:
          setValue(
            "role",
            _.find(options, (item) => item.value === userUp?.role)
          );
          break;

        case 2:
          setValue("enabled", userUp?.enabled ? "enabled" : "no-enabled");
          break;

        case 3:
          setValue("confirmed", userUp?.confirmed);
          break;

        default:
          return;
      }
    }
    // eslint-disable-next-line
  }, [userUp, valueTab]);

  const { handleSubmit, control, formState, setValue } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues:
      valueTab === 0
        ? {
          email: "",
        }
        : valueTab === 1
          ? { role: "" }
          : {},
  });

  const onFailure = useCallback((error) => {
    setLoading(false);
    dispatch(
      openSnackbar({
        open: true,
        message: error.response?.data?.message ? error.response.data.message : "Errore",

        variant: "alert",
        alert: {
          color: "error",
        },
        close: false,
      })
    );
    // eslint-disable-next-line
  }, []);

  const onError = useCallback(
    (error, data) => {
      setLoading(false);
      setDataSubmit(data);
      if (isFirstRefresh) {
        setLoading(false);
        if (error?.response?.status === 403) {
          dispatch(logout());
        } else {
          if (error?.response?.status !== 401) {
            onFailure(error);
          } else {
            if (isFirstRefresh) {
              dispatch(
                refreshToken({
                  grant_type: "refresh_token",
                  refresh_token: user.refresh_token,
                })
              );
              setIsFirstRefresh(false);
            }
          }
        }
      } else {
        onLogout(error);
      }
    },
    // eslint-disable-next-line
    []
  );

  const onSuccess = useCallback((resp) => {
    setLoading(false);
    setIsFirstRefresh(true);
    setSuccess(resp);
    dispatch(
      openSnackbar({
        open: true,
        message: resp.message,
        variant: "alert",
        alert: {
          color: resp.status,
        },
        close: false,
      })
    );
    handleCloseDialog()
    // eslint-disable-next-line
  }, []);

  const onLogout = useCallback((error) => {
    if (error?.response?.status === 403 || error?.response?.status === 401) {
      dispatch(logout());
    }
    // eslint-disable-next-line
  }, []);

  const onSubmit = useCallback(
    (data) => {
      switch (valueTab) {
        case 0:
          setLoading(true);
          Request.users
            .updateUserDetail(
              {
                id: id,
                token: token,
              },
              {
                email: data?.email,
                firstName: data?.firstName,
                lastName: data?.lastName,
              }
            )
            .then((resp) => onSuccess(resp))
            .catch((error) => onError(error, data));
          break;
        case 1:
          setLoading(true);
          Request.users
            .setRole({ user_id: id, role: data?.role.value, token: token })
            .then((resp) => onSuccess(resp))
            .catch((error) => onError(error, data));
          break;

        case 2:
          const enabled = data?.enabled === "no-enabled" ? false : true;
          const userEnabled = userUp?.enabled === null ? false : userUp?.enabled;

          if (userEnabled !== enabled) {
            setLoading(true);
            Request.users
              .setStatus({
                user_id: id,
                token: token,
              })
              .then((resp) => onSuccess(resp))
              .catch((error) => onError(error, data));
          }

          break;

        case 3:
          if (data?.confirmed) {
            setLoading(true);
            Request.users
              .setConfirm({
                user_id: id,
                token: token,
              })
              .then((resp) => onSuccess(resp))
              .catch((error) => onError(error, data));
          }
          break;
        default:
          return;
      }
    },
    // eslint-disable-next-line
    [id, token, valueTab, user, refresh_token]
  );

  useEffect(() => {
    switch (valueTab) {
      case 0:
        setValidationSchema(ValidationSchemaEmail);
        break;
      case 1:
        setValidationSchema(ValidationSchemaRole);
        break;
      case 2:
        setValidationSchema(ValidationSchemaEnable);
        break;
      case 3:
        setValidationSchema(ValidationSchemaConfirm);
        break;
      default:
        return;
    }
  }, [valueTab]);

  useEffect(() => {
    updateForm();
    // eslint-disable-next-line
  }, [user, valueTab]);

  useEffect(() => {
    if (!isFirstRefresh && !errorRefresh && !loadingRefresh) {
      onSubmit(dataSubmit);
    }
    // eslint-disable-next-line
  }, [isFirstRefresh, loadingRefresh, errorRefresh, dataSubmit]);

  return (
    <Fragment>
      {loading ? (
        <Loading />
      ) : (
        <SideDialogComponent open={open} handleCloseDialog={handleCloseDialog}>
          <Typography
            sx={{ padding: "16px 24px", fontWeight: 500, fontSize: 20 }}
          >
            {t("Modify user data")}
          </Typography>

          <TabsComponent
            value={valueTab}
            handleChange={handleChangeTab}
            tabs={[
              {
                label: t("User data"),
                component: (
                  <UserDetailForm
                    errors={formState.errors}
                    control={control}
                    handleSubmit={handleSubmit(onSubmit)}
                    handleCloseDialog={handleCloseDialog}
                    labelButton={t("Edit")}
                  />
                ),
              },
              {
                label: t("User role"),
                component: (
                  <UserRoleForm
                    errors={formState.errors}
                    control={control}
                    handleSubmit={handleSubmit(onSubmit)}
                    handleCloseDialog={handleCloseDialog}
                    labelButton={t("Edit")}
                  />
                ),
              },
              {
                label: t("User Enablement"),
                component: (
                  <UserEnableForm
                    errors={formState.errors}
                    control={control}
                    handleSubmit={handleSubmit(onSubmit)}
                    handleCloseDialog={handleCloseDialog}
                    labelButton={t("Edit")}
                  />
                ),
              },
              {
                label: t("Confirmation"),
                component: (
                  <UserConfirmForm
                    errors={formState.errors}
                    control={control}
                    handleSubmit={handleSubmit(onSubmit)}
                    handleCloseDialog={handleCloseDialog}
                    labelButton={t("Edit")}
                    userConfirm={userUp?.confirmed}
                  />
                ),
              },
            ]}
          />
        </SideDialogComponent>
      )}
    </Fragment>
  );
};

export default UpdateUserContainer;
